'use client';

import { usePolling } from '@/lib/usePolling';
import { Clock, Users, TrendingUp, Info } from 'lucide-react';

export interface CapacityData {
  total_capacity_minutes: number;
  total_booked_minutes: number;
  total_available_minutes: number;
  staffing_expected_minutes: number;
  staffing_p80_minutes: number;
  staffing_crew_day_expected_minutes: number;
  staffing_crew_day_p80_minutes: number;
  staffing_gusto_calibration_complete: boolean;
  staffing_gusto_calibration_applied: boolean;
  staffing_gusto_calibration_factor: number | null;
  staffing_gusto_sample_weeks: number;
  staffing_gusto_average_minutes: number | null;
  staffing_gusto_average_booked_minutes: number | null;
  staffing_gusto_adjustment_minutes: number;
  staffing_gusto_window_start: string | null;
  staffing_gusto_window_end: string | null;
  staffing_non_booked_minutes: number;
  staffing_service_capacity_minutes: number;
  staffing_peak_planned_people: number;
  staffing_projected_crew_days: number;
  staffing_projected_work_days: number;
  staffing_scheduled_crew_days: number;
  staffing_scheduled_work_days: number;
  staffing_projection_census_stable: boolean;
  staffing_projection_complete: boolean;
  active_employees: number;
  crews: { crew_id: string; name: string; booked_minutes: number; service_minutes: number; drive_minutes: number; deadhead_minutes: number }[];
  week_start: string;
  week_end: string;
}

const INITIAL: CapacityData = {
  total_capacity_minutes: 0,
  total_booked_minutes: 0,
  total_available_minutes: 0,
  staffing_expected_minutes: 0,
  staffing_p80_minutes: 0,
  staffing_crew_day_expected_minutes: 0,
  staffing_crew_day_p80_minutes: 0,
  staffing_gusto_calibration_complete: false,
  staffing_gusto_calibration_applied: false,
  staffing_gusto_calibration_factor: null,
  staffing_gusto_sample_weeks: 0,
  staffing_gusto_average_minutes: null,
  staffing_gusto_average_booked_minutes: null,
  staffing_gusto_adjustment_minutes: 0,
  staffing_gusto_window_start: null,
  staffing_gusto_window_end: null,
  staffing_non_booked_minutes: 0,
  staffing_service_capacity_minutes: 0,
  staffing_peak_planned_people: 0,
  staffing_projected_crew_days: 0,
  staffing_projected_work_days: 0,
  staffing_scheduled_crew_days: 0,
  staffing_scheduled_work_days: 0,
  staffing_projection_census_stable: false,
  staffing_projection_complete: false,
  active_employees: 0,
  crews: [],
  week_start: '',
  week_end: '',
};

function utilizationColor(pct: number, crewShort: boolean): {
  bar: string;
  text: string;
  bg: string;
  label: string;
} {
  if (pct > 100 || crewShort)
    return {
      bar: 'bg-rose-500',
      text: 'text-rose-400',
      bg: 'bg-rose-500/10',
      label: crewShort ? 'Crew Short' : 'Over Capacity',
    };
  if (pct >= 90)
    return {
      bar: 'bg-amber-500',
      text: 'text-amber-400',
      bg: 'bg-amber-500/10',
      label: 'Tight',
    };
  return {
    bar: 'bg-emerald-500',
    text: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    label: 'Available',
  };
}

function formatHoursShort(minutes: number): string {
  return `${Math.round(minutes / 60)}h`;
}

export function resolveWeeklyStaffingDemand(data: Pick<CapacityData,
  'staffing_projection_complete' | 'staffing_expected_minutes' | 'total_booked_minutes'
>): { minutes: number; usesCrewDayProjection: boolean } {
  const usesCrewDayProjection = data.staffing_projection_complete
    && data.staffing_expected_minutes > 0;
  return {
    minutes: usesCrewDayProjection
      ? data.staffing_expected_minutes
      : data.total_booked_minutes,
    usesCrewDayProjection,
  };
}

export function WeeklyCapacityWidget() {
  const { data, isLoading } = usePolling<CapacityData>({
    url: '/api/admin/capacity/weekly',
    intervalMs: 120_000,
    initialValue: INITIAL,
  });

  const {
    total_capacity_minutes,
    total_booked_minutes,
    staffing_p80_minutes,
    staffing_crew_day_expected_minutes,
    staffing_gusto_calibration_applied,
    staffing_gusto_calibration_factor,
    staffing_gusto_sample_weeks,
    staffing_gusto_average_minutes,
    staffing_gusto_average_booked_minutes,
    staffing_gusto_adjustment_minutes,
    staffing_gusto_window_start,
    staffing_gusto_window_end,
    staffing_non_booked_minutes,
    staffing_service_capacity_minutes,
    staffing_peak_planned_people,
    staffing_projected_crew_days,
    staffing_projected_work_days,
    staffing_scheduled_crew_days,
    staffing_projection_census_stable,
    active_employees,
  } = data;
  const resolvedDemand = resolveWeeklyStaffingDemand(data);
  const hasStaffingProjection = resolvedDemand.usesCrewDayProjection
    && staffing_projected_work_days > 0;
  const staffingDemandMinutes = resolvedDemand.minutes;
  const bookedWorkCapacityMinutes = staffing_service_capacity_minutes > 0
    ? staffing_service_capacity_minutes
    : Math.max(0, total_capacity_minutes - staffing_non_booked_minutes);
  const pct = bookedWorkCapacityMinutes > 0
    ? (staffingDemandMinutes / bookedWorkCapacityMinutes) * 100
    : 0;
  const clampedPct = Math.min(pct, 100);
  const remainingMinutes = bookedWorkCapacityMinutes - staffingDemandMinutes;
  const overtimeMinutes = Math.max(-remainingMinutes, 0);
  const bufferMinutes = Math.max(remainingMinutes, 0);
  const crewShort = hasStaffingProjection
    && staffing_peak_planned_people > active_employees;
  const colors = hasStaffingProjection
    ? utilizationColor(pct, crewShort)
    : {
        bar: 'bg-cyan-500',
        text: 'text-cyan-400',
        bg: 'bg-cyan-500/10',
        label: 'Budget Only',
      };

  if (isLoading && !data.week_start) {
    return (
      <div className="h-[72px] rounded-xl border border-white/5 bg-zinc-900/40 animate-pulse" />
    );
  }

  return (
    <div className="rounded-xl border border-white/5 bg-zinc-900/60 backdrop-blur-sm px-4 py-3 lg:col-span-2">
      {/* Header row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex min-w-0 items-center gap-2">
          <div className="p-1 rounded-md bg-zinc-800/60">
            <Clock className="w-3.5 h-3.5 text-zinc-400" />
          </div>
          <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
            Weekly Staffing
          </span>
          <span className="group relative ml-1">
            <button
              type="button"
              aria-describedby="weekly-staffing-provenance"
              aria-label="About weekly staffing"
              className="block rounded text-zinc-500 hover:text-zinc-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
            >
              <Info className="w-3 h-3" />
            </button>
            <span
              id="weekly-staffing-provenance"
              role="tooltip"
              className="pointer-events-none invisible absolute left-0 top-full z-20 mt-2 w-64 rounded-lg border border-white/10 bg-zinc-950 p-2 text-[10px] font-medium normal-case tracking-normal text-zinc-300 shadow-xl group-hover:visible group-focus-within:visible"
            >
              {staffing_gusto_calibration_applied
                ? `Expected booked work uses the higher of the crew-day plan and the price budget calibrated to ${staffing_gusto_sample_weeks} complete Gusto weeks. The ${formatHoursShort(staffing_gusto_average_minutes ?? 0)} paid average is reduced by ${formatHoursShort(staffing_non_booked_minutes)} of recurring other work before calibration (${staffing_gusto_calibration_factor?.toFixed(2)}×; ${staffing_gusto_window_start}–${staffing_gusto_window_end}).`
                : 'Expected and P80 paid labor come from complete current crew-day plans. The price budget remains a separate financial signal.'}
            </span>
          </span>
        </div>

        <span
          className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}
        >
          {colors.label}
        </span>
      </div>

      <div className="mb-2 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-2xl font-black leading-none tracking-tight text-white">
            {formatHoursShort(staffingDemandMinutes)}
          </div>
          <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
            {hasStaffingProjection
              ? staffing_gusto_calibration_applied ? 'Gusto-adjusted booked work' : 'Expected booked work'
              : 'Price budget'}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 text-right text-[10px] text-zinc-400">
          <Users className="h-3.5 w-3.5 text-zinc-500" />
          <span>
            {active_employees} worker{active_employees !== 1 ? 's' : ''} · {formatHoursShort(bookedWorkCapacityMinutes)} booked-work capacity
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative h-2.5 rounded-full bg-zinc-800/80 overflow-hidden">
        <div
          role="progressbar"
          aria-label={hasStaffingProjection
            ? 'Expected weekly booked-work load'
            : 'Price-derived weekly booking budget'}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(Math.min(pct, 100))}
          aria-valuetext={hasStaffingProjection
            ? `${Math.round(pct)}% of booked-work capacity`
            : `${Math.round(pct)}% of booked-work capacity`}
          className={`absolute inset-y-0 left-0 rounded-full transition-[width] duration-700 ease-out ${colors.bar}`}
          style={{ width: `${clampedPct}%` }}
        />
      </div>

      {/* Stats row */}
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-1.5">
          <TrendingUp className="w-3 h-3 text-zinc-500" />
          <span className="text-[10px] font-semibold text-zinc-300">
            {hasStaffingProjection
              ? `${Math.round(pct)}% utilized`
              : `${Math.round(pct)}% of booked-work capacity`}
          </span>
        </div>
        <span className={`shrink-0 text-[10px] font-semibold ${overtimeMinutes > 0 ? colors.text : 'text-zinc-500'}`}>
          {hasStaffingProjection
            ? overtimeMinutes > 0
              ? `${formatHoursShort(overtimeMinutes)} OT expected`
              : `${formatHoursShort(bufferMinutes)} buffer`
            : 'Forecast incomplete'}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-white/5 pt-2 text-[10px] leading-tight text-zinc-500">
        {hasStaffingProjection ? (
          <>
            {staffing_gusto_calibration_applied && (
              <>
                <span>
                  Gusto {formatHoursShort(staffing_gusto_average_minutes ?? 0)} − other {formatHoursShort(staffing_non_booked_minutes)} = {formatHoursShort(staffing_gusto_average_booked_minutes ?? 0)} comparable
                </span>
                <span aria-hidden="true">·</span>
              </>
            )}
            <span>Crew-day {formatHoursShort(staffing_crew_day_expected_minutes)}</span>
            {staffing_gusto_calibration_applied && staffing_gusto_adjustment_minutes > 0 && (
              <>
                <span aria-hidden="true">·</span>
                <span>Trend lift {formatHoursShort(staffing_gusto_adjustment_minutes)}</span>
              </>
            )}
            <span aria-hidden="true">·</span>
            <span>P80 {formatHoursShort(staffing_p80_minutes)}</span>
            <span aria-hidden="true">·</span>
            <span className={crewShort ? 'text-rose-400' : undefined}>
              Peak {staffing_peak_planned_people} people
            </span>
            <span aria-hidden="true">·</span>
            <span>Price budget {formatHoursShort(total_booked_minutes)}</span>
          </>
        ) : (
          <span>
            {!staffing_projection_census_stable && staffing_scheduled_crew_days > 0
              ? 'Schedule changed while forecasting; showing price budget.'
              : staffing_scheduled_crew_days > 0
              ? `Forecast coverage ${staffing_projected_crew_days}/${staffing_scheduled_crew_days} crew-days; showing price budget.`
              : 'Crew-day forecast unavailable; showing the price budget.'}
          </span>
        )}
      </div>
    </div>
  );
}
